import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/Brand";
import { LoadingState, ErrorState } from "@/components/common";
import { AccountStep, CodeStep, GarageStep, StepDots } from "@/components/signup/SignupSteps";
import { useAuth, GARAGE_KEY, homePathFor } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, setApiGarage } from "@/lib/api";
import { t } from "@/i18n";

function Aside() {
  return (
    <>
      <p className="font-display text-4xl font-extrabold leading-tight text-white">{t("p7.signup.title")}</p>
      <p className="mt-4 text-slate-300">{t("p7.pricing.subtitle")}</p>
    </>
  );
}

export default function Signup() {
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plans = useFetch("/public/plans");
  const platform = useFetch("/public/platform");
  const [signup, setSignup] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  if (me === undefined || (plans.loading && !plans.data) || (platform.loading && !platform.data)) return <LoadingState full />;
  const approval = platform.data?.signup_requires_approval !== false;
  if (me?.garage_requests?.some((r) => r.status === "pending")) return <Navigate to="/signup/status" replace />;
  if (me?.user.is_super_admin) return <AuthLayout title={t("p7.signup.title")}><Link to="/admin" className="text-teal-700">{t("landing.openApp")}</Link></AuthLayout>;

  const step = me && (approval || me.user.email_verified) ? "garage" : signup ? "verify" : "account";
  const onStarted = (data) => (data.approval ? setMe(data.me) : setSignup(data));

  const onCreated = async (data, f) => {
    setMe(data.me);
    if (data.pending_approval) return navigate("/signup/status", { replace: true });
    localStorage.setItem(GARAGE_KEY, data.garage_id);
    if (!data.needs_checkout) {
      toast.success(t("p7.signup.done", { n: data.trial_days }));
      navigate("/pro/onboarding", { replace: true });
      return;
    }
    setRedirecting(true);
    setApiGarage(data.garage_id);
    try {
      const { data: co } = await api.post("/garage/subscription/checkout", { plan_id: f.plan_id, interval: f.interval, origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      navigate("/pro/settings?tab=plan", { replace: true });
    }
  };

  return (
    <AuthLayout title={t("p7.signup.title")} subtitle={t("p7.signup.subtitle")} aside={<Aside />}>
      <div data-testid="signup-page">
        <StepDots step={step} steps={approval ? ["account", "garage"] : undefined} />
        {approval && step === "garage" && <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="signup-approval-note">{t("p7.approval.note")}</p>}
        {redirecting && <p className="mb-4 text-sm font-medium text-teal-700" data-testid="signup-redirecting">{t("p7.signup.toCheckout")}</p>}
        {step === "account" && <AccountStep onStarted={onStarted} />}
        {step === "verify" && <CodeStep signup={signup} onVerified={setMe} onRestart={() => setSignup(null)} />}
        {step === "garage" && (plans.error ? <ErrorState message={plans.error} onRetry={plans.reload} /> : (
          <>
            <p className="mb-4 text-xs text-slate-500" data-testid="signup-logged-in-as">{t("p7.signup.loggedInAs", { email: me.user.email })}</p>
            <GarageStep plans={plans.data || []} initialPlan={params.get("plan")} initialInterval={params.get("interval")} onCreated={onCreated} />
          </>
        ))}
        {!me && <p className="mt-8 text-sm text-slate-600">{t("p7.signup.haveAccount")} <Link to={`/login?next=${encodeURIComponent("/signup")}`} className="font-semibold text-teal-700" data-testid="signup-login-link">{t("p7.signup.login")}</Link></p>}
        {me && step === "garage" && <p className="mt-6 text-sm"><Link to={homePathFor(me)} className="text-slate-500 hover:text-slate-800" data-testid="signup-skip-link">{t("landing.openApp")}</Link></p>}
      </div>
    </AuthLayout>
  );
}
